import React from 'react';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = ({ onNavigate }) => {
    return (
        <div className="min-h-screen bg-gradient-to-br from-black via-zinc-900 to-black text-gray-100 p-4 font-poppins">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <button 
                        onClick={() => onNavigate('landing')}
                        className="inline-flex items-center text-zinc-400 hover:text-white mb-6 transition-colors"
                    >
                        <ArrowLeft size={16} className="mr-2" />
                        Back to Home
                    </button>
                    <h1 className="text-4xl font-bold text-white mb-4">Terms of Service</h1>
                    <p className="text-gray-400">Last updated: January 8, 2025</p>
                </div>

                {/* Content */}
                <div className="bg-zinc-900/50 border border-zinc-700 rounded-xl p-8 space-y-8">
                    
                    <section>
                        <h2 className="text-2xl font-semibold text-purple-400 mb-4">1. Acceptance of Terms</h2>
                        <p className="text-gray-300 leading-relaxed">
                            By accessing or using BLACKBOXcr ("Service"), operated by BLACKBOXis ("Company", "we", "us", or "our"), 
                            you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use our Service.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-purple-400 mb-4">2. Description of Service</h2>
                        <p className="text-gray-300 leading-relaxed mb-4">
                            BLACKBOXcr is a web-based application that allows users to create, edit, and export professional film and television credits. 
                            Our Service includes:
                        </p>
                        <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                            <li>Credit editing and formatting tools</li>
                            <li>Video export capabilities in various formats and resolutions</li>
                            <li>Project management and storage</li>
                            <li>Template libraries and customization options</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-purple-400 mb-4">3. User Accounts and Registration</h2>
                        <p className="text-gray-300 leading-relaxed mb-4">
                            To use certain features of our Service, you must create an account. You agree to:
                        </p>
                        <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                            <li>Provide accurate and complete registration information</li>
                            <li>Maintain the security of your account credentials</li>
                            <li>Notify us immediately of any unauthorized use of your account</li>
                            <li>Accept responsibility for all activities under your account</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-purple-400 mb-4">4. Subscription Plans and Payments</h2>
                        <div className="text-gray-300 leading-relaxed space-y-4">
                            <p><strong>Free Trial:</strong> We offer a 7-day free trial with limited features and watermarked exports.</p>
                            <p><strong>Paid Plans:</strong> Subscription fees are charged in advance and are non-refundable except as required by law.</p>
                            <p><strong>Auto-Renewal:</strong> Subscriptions automatically renew unless cancelled before the renewal date.</p>
                            <p><strong>Price Changes:</strong> We reserve the right to modify pricing with 30 days notice to existing subscribers.</p>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-purple-400 mb-4">5. Acceptable Use</h2>
                        <p className="text-gray-300 leading-relaxed mb-4">You agree not to:</p>
                        <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                            <li>Use the Service for any unlawful purpose or in violation of any applicable laws</li>
                            <li>Attempt to reverse engineer, decompile, or hack the Service</li>
                            <li>Upload or create content that infringes on intellectual property rights</li>
                            <li>Share account credentials or attempt to circumvent usage limitations</li>
                            <li>Use the Service to create defamatory, harmful, or inappropriate content</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-purple-400 mb-4">6. Intellectual Property</h2>
                        <div className="text-gray-300 leading-relaxed space-y-4">
                            <p><strong>Your Content:</strong> You retain ownership of all content you create using our Service. You grant us a limited license to process and store your content to provide the Service.</p>
                            <p><strong>Our Service:</strong> The BLACKBOXcr software, features, and related intellectual property are owned by BLACKBOXis and protected by copyright and other laws.</p>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-purple-400 mb-4">7. Data and Privacy</h2>
                        <p className="text-gray-300 leading-relaxed">
                            Your privacy is important to us. Please review our Privacy Policy, which governs how we collect, 
                            use, and protect your information when you use our Service.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-purple-400 mb-4">8. Service Availability</h2>
                        <p className="text-gray-300 leading-relaxed">
                            While we strive to maintain high availability, we do not guarantee uninterrupted access to our Service. 
                            We may suspend or terminate the Service for maintenance, updates, or other operational reasons with reasonable notice.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-purple-400 mb-4">9. Limitation of Liability</h2>
                        <p className="text-gray-300 leading-relaxed">
                            TO THE FULLEST EXTENT PERMITTED BY LAW, BLACKBOXis SHALL NOT BE LIABLE FOR ANY 
                            INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, 
                            DATA, OR USE, ARISING OUT OF OR RELATING TO YOUR USE OF THE SERVICE.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-purple-400 mb-4">10. Termination</h2>
                        <p className="text-gray-300 leading-relaxed">
                            Either party may terminate these Terms at any time. Upon termination, your right to use the Service ceases immediately. 
                            We may retain your data for a reasonable period as outlined in our Privacy Policy.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-purple-400 mb-4">11. Governing Law</h2>
                        <p className="text-gray-300 leading-relaxed">
                            These Terms are governed by the laws of the State of Florida, without regard to conflict of law principles. 
                            Any disputes shall be resolved in the courts of Florida.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-purple-400 mb-4">12. Changes to Terms</h2>
                        <p className="text-gray-300 leading-relaxed">
                            We may update these Terms from time to time. We will notify users of material changes via email or 
                            through the Service. Continued use after changes constitutes acceptance of the new Terms.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-purple-400 mb-4">13. Contact Information</h2>
                        <p className="text-gray-300 leading-relaxed">
                            For questions about these Terms of Service, please contact us at:
                            <br />
                            <strong>Email:</strong> <a href="mailto:support@blackbox-is.com" className="text-purple-400 hover:text-purple-300">support@blackbox-is.com</a>
                            <br />
                            <strong>Company:</strong> BLACKBOXis
                            <br />
                            <strong>Location:</strong> Florida, USA
                        </p>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default TermsOfService;